import { API } from '@/config/api';
import { ApiError } from '@/shared/api/ApiError';
import { apiClient } from '@/shared/api/apiClient';

import type { LinkedInTeamRenameRequest, LinkedInTeamResult } from '../types';

/**
 * Building a team is two model calls on the server, the prompt and then the team, each of which can
 * take a minute or more. The shared client's default would cut it off long before, so this is the
 * previous screen's three minutes.
 */
const BUILD_TIMEOUT_MS = 180_000;

/** The company's team and what was done to it, or null when it has not built one (LA-107). */
export async function fetchLinkedInTeam(): Promise<LinkedInTeamResult | null> {
  try {
    return await apiClient.get<LinkedInTeamResult>(API.linkedinAgentTeam);
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) return null;
    throw error;
  }
}

/**
 * Builds the team from the saved configuration, or returns the existing one with `alreadyExists`
 * true, in which case the organization agent is not asked (LA-101).
 */
export async function createLinkedInTeam(): Promise<LinkedInTeamResult> {
  return apiClient.post<LinkedInTeamResult>(API.linkedinAgentTeam, undefined, {
    timeout: BUILD_TIMEOUT_MS,
  });
}

/**
 * Stores a made-up team, for local testing. The server has this only in its `local` profile: anywhere
 * else the path is a 404. Nothing calls it unless the screen offers the button, which it does only in a
 * development build.
 */
export async function createDummyLinkedInTeam(): Promise<LinkedInTeamResult> {
  return apiClient.post<LinkedInTeamResult>(`${API.linkedinAgentTeam}/dummy`);
}

/** Builds the team again from the latest configuration, with a new strategy prompt (LA-102). */
export async function regenerateLinkedInTeam(): Promise<LinkedInTeamResult> {
  return apiClient.post<LinkedInTeamResult>(API.linkedinAgentTeamRegenerate, undefined, {
    timeout: BUILD_TIMEOUT_MS,
  });
}

/** Renames the leader and members. Nothing but names changes (LA-100). */
export async function renameLinkedInTeam(
  teamId: string,
  request: LinkedInTeamRenameRequest,
): Promise<LinkedInTeamResult> {
  return apiClient.put<LinkedInTeamResult>(API.linkedinAgentTeamById(teamId), request);
}

/** Replaces the strategy prompt. A reason is required and is kept with its time (LA-106). */
export async function updateLinkedInTeamStrategy(
  teamId: string,
  strategyPrompt: string,
  note: string,
): Promise<LinkedInTeamResult> {
  return apiClient.put<LinkedInTeamResult>(API.linkedinAgentTeamStrategy(teamId), {
    strategyPrompt,
    note,
  });
}
